import { EmailHistory } from '../email/EmailHistory';
import AccountBalanceWalletRounded from '@mui/icons-material/AccountBalanceWalletRounded';
import BlockRounded from '@mui/icons-material/BlockRounded';
import EventRounded from '@mui/icons-material/EventRounded';
import PaymentsRounded from '@mui/icons-material/PaymentsRounded';
import ReceiptLongRounded from '@mui/icons-material/ReceiptLongRounded';
import { Alert, Box, Button, Link, Tooltip, Typography } from '@mui/material';
import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, type Column } from '../../components/DataTable';
import { Money, Qty } from '../../components/Money';
import { businessDate, dateTime, money } from '../../lib/format';
import { supplierBillDoc, WhatsappA4, WhatsappHistory } from '../whatsapp';
import { AttachmentsPanel } from '../attachments';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  purchaseApi,
  usePurchaseOrder,
  usePurchasesMutation,
  useSupplierInvoice,
  type SupplierInvoice,
  type SupplierInvoiceLine,
} from './api';
import {
  BillStatusChip,
  DetailSkeleton,
  DocHeader,
  ErrorState,
  Facts,
  ProgressMeter,
  ReasonDialog,
  Section,
  SummaryCard,
  SummaryGrid,
  TotalsRows,
} from './components/ui';
import { add, compare } from './lib/decimal';
import { paidPct } from './lib/progress';
import { MoreMenu } from '../../components/MoreMenu';
import { useLinked, useLinkedOne } from '../../components/useLinked';
import { useLinkedPayments } from '../../components/useLinkedPayments';

export function SupplierBillDetailPage() {
  const { id } = useParams();
  const bill = useSupplierInvoice(id);
  if (bill.isLoading) return <DetailSkeleton />;
  if (bill.error || !bill.data)
    return <ErrorState error={bill.error} onRetry={() => void bill.refetch()} />;
  return <BillDetail bill={bill.data} />;
}

function BillDetail({ bill }: { bill: SupplierInvoice }) {
  const { can } = useAuth();
  const { format } = useShop();
  const notify = useNotify();
  const [cancelling, setCancelling] = useState(false);
  const order = usePurchaseOrder(bill.purchaseOrderId ?? undefined);
  const linkedOrder = useLinkedOne('purchaseOrder', bill.purchaseOrderId);
  const receipts = useLinked('receipts', { supplierInvoiceId: bill.id });
  const payments = useLinkedPayments({ supplierInvoiceId: bill.id });
  const debitNotes = useLinked('debitNotes', { supplierInvoiceId: bill.id });
  const posted = bill.status === 'POSTED';
  const paid = compare(bill.amountPaid, '0') > 0;
  const variance = compare(bill.priceVariance, '0');

  const cancel = usePurchasesMutation((reason: string) =>
    purchaseApi.cancelBill(bill.id, bill.version, reason),
  );

  const columns: Column<SupplierInvoiceLine>[] = [
    {
      key: 'item',
      header: 'Description',
      render: (l) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {l.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {l.sku ?? (l.accountId ? 'Other cost' : '')}
            {l.goodsReceiptLineId && compare(l.grniAmount, '0') > 0 && can('products.view_cost') ? (
              <>
                {' · receipt value '}
                <Money value={l.grniAmount} />
              </>
            ) : null}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'qty',
      header: 'Qty',
      align: 'right',
      render: (l) => <Qty value={l.qty} unit={l.unitSymbol ?? undefined} />,
    },
    {
      key: 'price',
      header: 'Unit price',
      align: 'right',
      hideBelow: 'md',
      render: (l) => <Money value={l.unitPrice} />,
    },
    {
      key: 'disc',
      header: 'Discount',
      align: 'right',
      hideBelow: 'lg',
      render: (l) =>
        compare(l.discountAmount, '0') + compare(l.documentDiscount, '0') > 0 ? (
          <Typography variant="body2" color="success.main">
            − <Money value={add(l.discountAmount, l.documentDiscount)} />
          </Typography>
        ) : (
          '—'
        ),
    },
    {
      key: 'tax',
      header: 'Tax',
      align: 'right',
      hideBelow: 'lg',
      render: (l) =>
        compare(l.taxAmount, '0') > 0 ? (
          <Box>
            <Money value={l.taxAmount} />
            <Typography variant="caption" color="text.secondary" component="div">
              {Number(l.taxRate)}%
            </Typography>
          </Box>
        ) : (
          '—'
        ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (l) => <Money value={l.lineTotal} strong />,
    },
  ];

  return (
    <Box>
      <DocHeader
        back={
          bill.purchaseOrderId
            ? {
                to: `/purchases/orders/${bill.purchaseOrderId}`,
                label: order.data?.number ?? 'Purchase order',
              }
            : { to: '/purchases?tab=bills', label: 'Supplier bills' }
        }
        eyebrow="Supplier bill"
        title={bill.number}
        chips={
          <BillStatusChip
            status={bill.status}
            paymentStatus={bill.paymentStatus}
            overdue={bill.overdue}
          />
        }
        subtitle={
          <>
            <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {bill.contact.name}
            </Box>
            {bill.supplierInvoiceNumber ? ` · Invoice ${bill.supplierInvoiceNumber}` : ''}
            {' · '}
            {businessDate(bill.docDate, format)}
          </>
        }
        actions={
          <>
            {posted && (
              <>
                <WhatsappA4
                  type="supplier_invoice"
                  id={bill.id}
                  number={bill.number}
                  contact={bill.contact}
                  vars={{
                    amount: money(bill.total, format),
                    date: businessDate(bill.docDate, format),
                  }}
                  doc={() => supplierBillDoc(bill, format)}
                />
                {compare(bill.outstanding, '0') > 0 && can('payments.create') && (
                  <Button
                    variant="contained"
                    startIcon={<PaymentsRounded />}
                    component={RouterLink}
                    to={`/payments/new?purpose=SUPPLIER_PAYMENT&contactId=${bill.contact.id}&invoiceId=${bill.id}`}
                  >
                    Record payment
                  </Button>
                )}
                {can('purchases.cancel') && (
                  <Tooltip title={paid ? 'Remove its payments first' : ''}>
                    <span>
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<BlockRounded />}
                        disabled={paid}
                        onClick={() => setCancelling(true)}
                      >
                        Cancel bill
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </>
            )}
            <MoreMenu
              linked={[linkedOrder, receipts, payments, debitNotes]}
              exportModel="supplier_bill"
              exportId={bill.id}
            />
          </>
        }
      />

      {bill.status === 'CANCELLED' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Cancelled {dateTime(bill.cancelledAt, format)}
          {bill.cancelReason ? ` — ${bill.cancelReason}` : ''}. Its quantities can be billed again.
        </Alert>
      )}

      <SummaryGrid>
        <SummaryCard label="Bill total" icon={<ReceiptLongRounded />}>
          <Money value={bill.total} />
          {compare(bill.taxTotal, '0') > 0 && (
            <Typography variant="caption" color="text.secondary" component="div">
              incl. <Money value={bill.taxTotal} /> tax
            </Typography>
          )}
        </SummaryCard>
        <SummaryCard label="Paid" icon={<PaymentsRounded />} accent="success">
          <ProgressMeter
            label={money(bill.amountPaid, format)}
            value={paidPct(bill.total, bill.amountPaid)}
            color="success"
          />
        </SummaryCard>
        <SummaryCard
          label="Balance due"
          icon={<AccountBalanceWalletRounded />}
          accent={bill.overdue ? 'error' : 'warning'}
        >
          {bill.status === 'CANCELLED' ? '—' : <Money value={bill.outstanding} />}
        </SummaryCard>
        <SummaryCard label="Due" icon={<EventRounded />} accent={bill.overdue ? 'error' : 'info'}>
          <Box component="span" sx={{ color: bill.overdue ? 'error.main' : undefined }}>
            {businessDate(bill.dueDate, format)}
          </Box>
          {bill.overdue && (
            <Typography variant="caption" color="error.main" component="div">
              Overdue
            </Typography>
          )}
        </SummaryCard>
      </SummaryGrid>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: 'minmax(0,1fr) 320px' },
          alignItems: 'start',
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <DataTable columns={columns} rows={bill.lines} rowKey={(l) => l.id} />
        </Box>
        <Box>
          <Section title="Summary">
            <TotalsRows
              rows={[
                { label: 'Subtotal', value: bill.subtotal },
                ...(compare(bill.discountTotal, '0') > 0
                  ? [{ label: 'Discounts', value: bill.discountTotal, negative: true }]
                  : []),
                ...(compare(bill.taxTotal, '0') > 0
                  ? [{ label: 'Tax', value: bill.taxTotal }]
                  : []),
              ]}
              total={bill.total}
            />
            {can('products.view_cost') && compare(bill.grniAmount, '0') > 0 && (
              <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary" component="div">
                  Receipt value cleared <Money value={bill.grniAmount} />
                </Typography>
                {variance !== 0 && (
                  <Typography
                    variant="caption"
                    component="div"
                    color={variance > 0 ? 'error.main' : 'success.main'}
                  >
                    {variance > 0 ? 'Paid more than received cost by ' : 'Saved on received cost: '}
                    <Money value={bill.priceVariance.replace('-', '')} />
                  </Typography>
                )}
              </Box>
            )}
          </Section>
          <Section title="Details">
            <Facts
              items={[
                ['Supplier', bill.contact.name],
                ...(bill.purchaseOrderId
                  ? [
                      [
                        'Order',
                        <Link
                          key="o"
                          component={RouterLink}
                          to={`/purchases/orders/${bill.purchaseOrderId}`}
                        >
                          {order.data?.number ?? 'Purchase order'}
                        </Link>,
                      ] as [string, ReactNode],
                    ]
                  : []),
                ...(bill.reference ? [['Reference', bill.reference] as [string, string]] : []),
                ['Posted', dateTime(bill.postedAt, format)],
              ]}
            />
            {bill.notes && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 2, whiteSpace: 'pre-wrap' }}
              >
                {bill.notes}
              </Typography>
            )}
          </Section>
        </Box>
      </Box>

      <ReasonDialog
        open={cancelling}
        title={`Cancel ${bill.number}?`}
        body={
          <>
            An exact reversing entry removes <Money value={bill.total} /> from what you owe{' '}
            {bill.contact.name}. The goods can be billed again.
          </>
        }
        confirmLabel="Cancel bill"
        onConfirm={(reason) =>
          cancel.mutateAsync(reason).then(() => notify.success(`${bill.number} cancelled`))
        }
        onClose={() => setCancelling(false)}
      />
      <AttachmentsPanel recordType="SUPPLIER_BILL" recordId={bill.id} sx={{ mt: 3 }} />
      <WhatsappHistory documentType="supplier_invoice" documentId={bill.id} sx={{ mt: 3 }} />
      <EmailHistory documentType="supplier_invoice" documentId={bill.id} sx={{ mt: 3 }} />
    </Box>
  );
}
